import React, {
  useMemo,
  useState,
  useCallback,
  useRef,
  useEffect,
  ReactNode,
  SetStateAction,
  Dispatch,
} from 'react'
import { Helmet as HelmetBase } from 'react-helmet'
import { Outlet, useLocation, useNavigate } from 'react-router'
import useMedia from 'react-use/lib/useMedia'
import useNetworkState from 'react-use/lib/useNetworkState'
import styled from 'styled-components'
import { AppError, errors } from '~/prix'
import Handle from '~/prix/react/components/handle'
import { scrollContext } from '~/prix/react/components/scrollContext'
import { useAutoSetViewMode } from '~/prix/react/hooks/viewMode'
import Head from './head'
import Screen from './screen'

const Helmet = HelmetBase as unknown as React.ComponentType<any>

const Outer = styled.div<{
  $mobileIsVisibleMenu?: boolean
}>`
  display: flex;
  flex: 1;
  flex-direction: column;
  max-width: 100%;

  .wrapper {
    position: relative;
    display: flex;
    flex: 1;
    flex-direction: row;
    order: 2;
    height: 100%;
    overflow: hidden;

    > main {
      display: ${({ $mobileIsVisibleMenu }: { $mobileIsVisibleMenu?: boolean }) =>
        $mobileIsVisibleMenu ? 'none' : 'flex'};
      flex: 1;
      order: 3;
      position: relative;
      flex-direction: column;
      overflow: auto;
      background-color: #fafafa;
      z-index: 1;
    }

    /* div.menu-wrapper {
      display: flex;
      flex: 1;
      order: 2;
      z-index: 2;
      min-width: 275px;
      box-shadow: 1px 0 2px rgba(0, 0, 0, 0.1);
      position: relative;
      flex-direction: column;
      overflow-x: hidden;

      border: 2px solid blue;

      scrollbar-color: #ccc #fafafa;

      &::-webkit-scrollbar {
        width: 8px;
      }

      &::-webkit-scrollbar-track {
        background-color: #fafafa;
      }

      &::-webkit-scrollbar-thumb {
        background-color: #ccc;
        border-radius: 4px;
        border: 2px solid #fafafa;
      }

      @media (max-width: 870px) {
        display: ${({ $mobileIsVisibleMenu }: { $mobileIsVisibleMenu?: boolean }) =>
      $mobileIsVisibleMenu ? 'flex' : 'none'};
        background-color: #fff;
      }
    } */

    div.menu-wrapper {
      display: flex;
      /* flex: 1; */
      order: 2;
      z-index: 2;
      min-width: 320px;
      max-width: 375px;
      box-shadow: 1px 0 2px rgba(0, 0, 0, 0.1);
      position: relative;
      flex-direction: column;
      /* overflow-x: hidden; */
      transition: all 0.25s ease;

      @media (max-width: 870px) {
        display: ${({ $mobileIsVisibleMenu }: { $mobileIsVisibleMenu?: boolean }) =>
          $mobileIsVisibleMenu ? 'flex' : 'none'};
        background-color: #fff;
        /* min-width: 300px; */
        max-width: unset;
        box-shadow: none;
        /* margin: 0 auto; */
      }
    }

    @media (max-width: 870px) {
      flex-direction: column;
    }
  }
`

export interface ICard {
  title?: string
  outOfRangeEntities?: number | string
  totalEntities?: number | string
  onToggleMenu?: any
  selectedLegalEntity?: {
    selectedLegalEntityId: string
    selectedLegalEntityName: string
  }
  customStyles?: any
  dataSource?: string
  setDataSource?: Dispatch<SetStateAction<string>>
}

export interface AppLayoutProps {
  title?: string
  children?: React.ReactNode
  error?: Error | AppError | null
  mustBeAuthenticated?: boolean
  isLoading?: boolean
  menuDeskWidth?: number
  menu?: ReactNode | (({ onToggleMenu }: { onToggleMenu: () => void }) => ReactNode) | null
  initialMenuVisibility?: boolean
  isVisibleOffline?: boolean
  isRadar?: boolean
  isDashboard?: boolean
  card?: ICard | null
  isLoadingIndicatorsData?: boolean
  onlyChildren?: boolean
}

export default function AppLayout({
  children,
  title,
  isLoading = false,
  error,
  initialMenuVisibility = false,
  isVisibleOffline = false,
  mustBeAuthenticated = false,
  menuDeskWidth,
  menu = null,
  onlyChildren = false,
}: AppLayoutProps) {
  const currentViewMode = useAutoSetViewMode()
  const location = useLocation()
  const mainReference = useRef<HTMLElement>(null)
  const isMobile = useMedia('(max-width: 870px)')
  const [mobileIsVisibleMenu, setMobileIsVisibleMenu] = useState(!initialMenuVisibility)
  const { online } = useNetworkState()
  const viewModeStorage = sessionStorage.getItem('viewMode')

  useEffect(() => {
    if (isMobile) {
      setMobileIsVisibleMenu(false)
    }
  }, [location])

  const networkError = useMemo(() => {
    if (!isVisibleOffline && !online) {
      return errors.connectionFailed()
    }
    return null
  }, [isVisibleOffline, online])

  const handleToggleMenu = useCallback(() => {
    setMobileIsVisibleMenu(old => !old)
  }, [])

  useEffect(() => {
    handleToggleMenu()
  }, [initialMenuVisibility])

  useEffect(() => {
    if (!isMobile) {
      setMobileIsVisibleMenu(false)
    }
  }, [isMobile])

  return (
    <Screen mustBeAuthenticated={mustBeAuthenticated}>
      <Outer $mobileIsVisibleMenu={isMobile ? mobileIsVisibleMenu : false}>
        <style>
          {`
          html {
            overflow-x: hidden;
            overflow-y: hidden;
          }
        `}
        </style>

        <div className='wrapper'>
          {/* <Helmet>{title ? <title>{title}</title> : null}</Helmet> */}

          <main ref={mainReference} style={{ flex: menu ? 3 : 5 }}>
            <scrollContext.Provider value={mainReference} />

            <Handle isLoading={isLoading} error={networkError || error}>
              <>
                {children}
                {!onlyChildren && <Outlet />}
              </>
            </Handle>
          </main>
          <>
            {menu ? (
              <div
                className='menu-wrapper'
                style={
                  !isMobile && menuDeskWidth
                    ? { minWidth: menuDeskWidth, width: menuDeskWidth }
                    : { flex: 1, overflow: 'hidden' }
                }
              >
                {typeof menu === 'function'
                  ? menu({
                      onToggleMenu: handleToggleMenu,
                    })
                  : menu}
              </div>
            ) : null}

            <Head
              title={title}
              isVisibleMenu={isMobile ? mobileIsVisibleMenu : false}
              onToggleMenu={handleToggleMenu}
              menu={!!menu}
            />
          </>
        </div>
      </Outer>
    </Screen>
  )
}
